import type { SelectOption } from '@web/components/select';

import { useAbortController } from '@packages/core/http';
import {
    type FailCodeEntity,
    type FailCodeSectionEntity,
    type FailCodeSubsectionEntity,
    FetchSubsectionsByFailCode,
    ResolveSectionAndSubsection,
    type ResolveSectionAndSubsectionResult,
    SearchFailCodes,
} from '@packages/domain/fail-codes';
import { container } from '@web/services/container';
import { useCallback, useRef, useState } from 'react';

import type { FailCodeSelection, FailCodeVehicleParams } from '../fail-codes-modal.types';

/**
 * Presentation-layer option for the fail code search typeahead. Wraps a domain {@link FailCode}
 * with a display label so the typeahead can render it without knowing the domain shape.
 */
export type FailCodeSearchOption = {
    /** The underlying fail code object from the API. */
    failCode: FailCodeEntity;

    /** Display label combining the fail code and description (e.g. "A1234 - Engine misfire"). */
    label: string;
};

// Operations are prefixed with $ to make them visually distinct in this file.
const [$fetchSubsectionsByFailCode, $resolveSectionAndSubsection, $searchFailCodes] = container.resolveOperations(
    FetchSubsectionsByFailCode,
    ResolveSectionAndSubsection,
    SearchFailCodes
);

/**
 * Manages the cascading state machine for the "Search by Fail Code" flow in the fail codes modal.
 *
 * The flow is: user types a search query -> results are shown -> user selects a fail code ->
 * sections and subsections are auto-resolved (if exactly one option exists at each level, it is
 * auto-selected; otherwise the user picks manually).
 *
 * This hook owns all state, use case calls, and presentation mapping. The consuming component
 * is a thin presentational shell that renders typeaheads and selects with the hook's data.
 *
 * @param vehicleParams - vehicle identification params used to scope fail code lookups
 * @returns hook state and handlers for the search-by-fail-code flow
 */
export function useSearchByFailCode(vehicleParams: FailCodeVehicleParams) {
    // State
    const [isSearching, setIsSearching] = useState(false);
    const [isResolving, setIsResolving] = useState(false);
    const [searchOptions, setSearchOptions] = useState<FailCodeSearchOption[]>([]);
    const [selectedFailCode, setSelectedFailCode] = useState<FailCodeSearchOption | null>(null);
    const [sectionOptions, setSectionOptions] = useState<SelectOption[]>([]);
    const [selectedSection, setSelectedSection] = useState<FailCodeSectionEntity | null>(null);
    const [subsectionOptions, setSubsectionOptions] = useState<SelectOption[]>([]);
    const [selectedSubsection, setSelectedSubsection] = useState<FailCodeSubsectionEntity | null>(null);
    // Exception: typed as `any` because this ref is attached to an AsyncTypeahead component
    // instance, and the hook should not depend on react-bootstrap-typeahead types directly.
    // The hook only calls .blur(), .hideMenu(), and .toggleMenu() on the ref value.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const searchInputRef = useRef<any>(null);

    // Hooks
    const { abortSignalRef } = useAbortController();

    // Handlers

    /**
     * Search for fail codes matching the user's query text. Updates the search options
     * with the results, mapped to typeahead-compatible options.
     *
     * @param query - the text the user typed into the search field
     */
    const handleSearch = useCallback(
        async (query: string) => {
            setIsSearching(true);
            try {
                const result = await $searchFailCodes({
                    modelCode: vehicleParams.modelCode,
                    modelYear: vehicleParams.modelYear,
                    optionCode: vehicleParams.optionCode,
                    query,
                    signal: abortSignalRef.current,
                });

                // Map domain entities to typeahead options
                const options = result.failCodes.map(mapFailCodeToSearchOption);
                setSearchOptions(options);

                // Toggle the menu open if it isn't already visible
                const el = document.getElementById('fail-code-search');
                if (!el) {
                    searchInputRef.current?.toggleMenu();
                }
            } catch {
                // Silently handle errors; the user can retry their search
            } finally {
                setIsSearching(false);
            }
        },
        [abortSignalRef, vehicleParams.modelCode, vehicleParams.modelYear, vehicleParams.optionCode]
    );

    /**
     * Handle the user selecting a fail code from the search results. Clears downstream
     * state (section/subsection) and triggers the auto-resolution cascade via the
     * `ResolveSectionAndSubsection` use case.
     *
     * @param option - the selected fail code option, or null to clear the selection
     */
    const handleFailCodeSelect = useCallback(
        async (option: FailCodeSearchOption | null) => {
            // Clear downstream state
            setSectionOptions([]);
            setSelectedSection(null);
            setSubsectionOptions([]);
            setSelectedSubsection(null);
            setSelectedFailCode(option);

            if (!option) {
                return;
            }

            setIsResolving(true);
            try {
                const result = await $resolveSectionAndSubsection({
                    failCode: option.failCode.failCode,
                    modelCode: vehicleParams.modelCode,
                    modelYear: vehicleParams.modelYear,
                    optionCode: vehicleParams.optionCode,
                    signal: abortSignalRef.current,
                });

                // Apply the resolution result to state
                applyResolutionResult(result);
            } catch {
                // Silently handle errors; section/subsection selects remain empty
            } finally {
                setIsResolving(false);
            }
        },
        [abortSignalRef, vehicleParams.modelCode, vehicleParams.modelYear, vehicleParams.optionCode]
    );

    /**
     * Handle the user selecting a section from the section dropdown. Clears the subsection
     * state and fetches subsections for the newly selected section using the currently
     * selected fail code and vehicle params.
     *
     * @param sectionJson - the JSON-stringified FailCodeSection, or empty string to clear
     */
    const handleSectionChange = useCallback(
        async (sectionJson: string) => {
            // Clear downstream subsection state
            setSubsectionOptions([]);
            setSelectedSubsection(null);

            if (!sectionJson) {
                setSelectedSection(null);
                return;
            }

            const section: FailCodeSectionEntity = JSON.parse(sectionJson);
            setSelectedSection(section);

            // Fetch subsections for the selected section if a fail code is selected
            if (!selectedFailCode) {
                return;
            }

            setIsResolving(true);
            try {
                const subsections = await $fetchSubsectionsByFailCode({
                    failCode: selectedFailCode.failCode.failCode,
                    modelCode: vehicleParams.modelCode,
                    modelYear: vehicleParams.modelYear,
                    optionCode: vehicleParams.optionCode,
                    sectionName: section.sectionName,
                    sectionNumber: section.sectionNumber,
                    signal: abortSignalRef.current,
                });

                // Map subsections to select options
                const options = subsections.map(mapSubsectionToSelectOption);
                setSubsectionOptions(options);
            } catch {
                // Silently handle errors; the subsection dropdown remains empty
            } finally {
                setIsResolving(false);
            }
        },
        [abortSignalRef, selectedFailCode, vehicleParams.modelCode, vehicleParams.modelYear, vehicleParams.optionCode]
    );

    /**
     * Handle the user selecting a subsection from the subsection dropdown.
     *
     * @param subsectionJson - the JSON-stringified FailCodeSubsection, or empty string to clear
     */
    const handleSubsectionChange = useCallback((subsectionJson: string) => {
        if (!subsectionJson) {
            setSelectedSubsection(null);
            return;
        }

        const subsection: FailCodeSubsectionEntity = JSON.parse(subsectionJson);
        setSelectedSubsection(subsection);
    }, []);

    /**
     * Build the final FailCodeSelection from the current state. Returns null if the
     * selection is incomplete (missing fail code, section, or subsection).
     *
     * @returns the confirmed selection, or null if incomplete
     */
    function confirmSelection(): FailCodeSelection | null {
        if (!selectedFailCode || !selectedSection || !selectedSubsection) {
            return null;
        }

        return {
            failCode: selectedFailCode.failCode.failCode,
            repairCode: selectedFailCode.failCode.failCode,
            section: selectedSection.sectionName,
            subSection: selectedSubsection.subsectionName,
        };
    }

    /**
     * Apply the result of the section/subsection resolution to component state. Builds
     * select options from the returned data and auto-selects values when the use case
     * determined only one option existed.
     *
     * @param result - the resolution result from the ResolveSectionAndSubsection use case
     */
    function applyResolutionResult(result: ResolveSectionAndSubsectionResult): void {
        // Build section select options with a placeholder
        const sectionSelectOptions: SelectOption[] = result.sectionData.map(mapSectionToSelectOption);
        setSectionOptions(sectionSelectOptions);

        // Auto-select section if the use case determined only one existed
        if (result.autoSelectedSection) {
            setSelectedSection(result.autoSelectedSection);
        }

        // Build subsection select options if subsections were fetched
        if (result.subsectionData) {
            const subsectionSelectOptions: SelectOption[] = result.subsectionData.map(mapSubsectionToSelectOption);
            setSubsectionOptions(subsectionSelectOptions);
        }

        // Auto-select subsection if the use case determined only one existed
        if (result.autoSelectedSubsection) {
            setSelectedSubsection(result.autoSelectedSubsection);
        }
    }

    // Derived state

    /** Whether the confirm button should be disabled (selection is incomplete). */
    const isConfirmDisabled =
        !selectedFailCode || (Boolean(selectedFailCode.failCode.failCode) && (!selectedSection || !selectedSubsection));
    return {
        /** Build the final FailCodeSelection, or null if incomplete. */
        confirmSelection,

        /** Whether the user selected a fail code (showing section/subsection selects). */
        hasSelectedFailCode: Boolean(selectedFailCode),

        /** Handler for the user selecting a fail code from the typeahead results. */
        handleFailCodeSelect,

        /** Handler for the user typing in the search input. */
        handleSearch,

        /** Handler for the user selecting a section from the dropdown. */
        handleSectionChange,

        /** Handler for the user selecting a subsection from the dropdown. */
        handleSubsectionChange,

        /** Whether the confirm button should be disabled. */
        isConfirmDisabled,

        /** Whether section/subsection resolution is in progress. */
        isResolving,

        /** Whether a fail code search request is in progress. */
        isSearching,

        /** The typeahead search result options. */
        searchOptions,

        /** Ref to attach to the AsyncTypeahead for programmatic menu control. */
        searchInputRef,

        /** The select options for the section dropdown. */
        sectionOptions,

        /** The currently selected fail code option. */
        selectedFailCode,

        /** The currently selected section (raw domain object for the select value). */
        selectedSection,

        /** The currently selected subsection (raw domain object for the select value). */
        selectedSubsection,

        /** The select options for the subsection dropdown. */
        subsectionOptions,
    };
}

/**
 * Map a domain FailCode to a typeahead search option with a display label.
 *
 * @param failCode - the fail code from the API
 * @returns a search option with the fail code wrapped and a formatted label
 * @example
 * ```ts
 * const option = mapFailCodeToSearchOption({ failCode: 'A1234', description: 'Engine misfire' });
 * // { failCode: { failCode: 'A1234', description: 'Engine misfire' }, label: 'A1234 - Engine misfire' }
 * ```
 */
function mapFailCodeToSearchOption(failCode: FailCodeEntity): FailCodeSearchOption {
    return {
        failCode,
        label: `${failCode.failCode} - ${failCode.description}`,
    };
}

/**
 * Map a FailCodeSection to a SelectOption for the section dropdown.
 *
 * @param section - the section from the API
 * @returns a select option with the section name as title and the JSON-stringified section as value
 * @example
 * ```ts
 * const option = mapSectionToSelectOption({ sectionName: 'Body Exterior', sectionNumber: 1 });
 * // { title: 'Body Exterior', value: '{"sectionName":"Body Exterior","sectionNumber":1}' }
 * ```
 */
function mapSectionToSelectOption(section: FailCodeSectionEntity): SelectOption {
    return {
        title: section.sectionName,
        value: JSON.stringify(section),
    };
}

/**
 * Map a FailCodeSubsection to a SelectOption for the subsection dropdown.
 *
 * @param subsection - the subsection from the API
 * @returns a select option with the subsection name as title and the JSON-stringified subsection as value
 * @example
 * ```ts
 * const option = mapSubsectionToSelectOption({ subsectionName: 'Front Door', subsectionNumber: 2 });
 * // { title: 'Front Door', value: '{"subsectionName":"Front Door","subsectionNumber":2}' }
 * ```
 */
function mapSubsectionToSelectOption(subsection: FailCodeSubsectionEntity): SelectOption {
    return {
        title: subsection.subsectionName,
        value: JSON.stringify(subsection),
    };
}
